import React, { useState, useEffect } from 'react';
import { FiBell } from 'react-icons/fi';
import axios from 'axios';
import API_URL from '../config/api';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
    // Actualizar cada 30 segundos
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // ✅ CORREGIDO: El backend envía { count } directamente
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('❌ Error fetching unread count:', error);
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/notifications?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📨 Notificaciones recientes:', response.data); // Para debug
      
      // ✅ CORREGIDO: El backend envía { notifications } no { data }
      let notificationsData = [];
      
      if (response.data?.notifications) {
        notificationsData = response.data.notifications;
      } else if (response.data?.data) {
        notificationsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        notificationsData = response.data;
      }
      
      setNotifications(notificationsData);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
    }
  };

  const handleBellClick = async () => {
    if (!showDropdown) {
      await fetchRecentNotifications();
    }
    setShowDropdown(!showDropdown);
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate('/notifications');
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diff = Math.floor((now - notifDate) / 60000); // minutos
    
    if (diff < 1) return 'Ahora';
    if (diff < 60) return `Hace ${diff} min`;
    if (diff < 1440) return `Hace ${Math.floor(diff / 60)} h`;
    return `Hace ${Math.floor(diff / 1440)} d`;
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'urgente': return '🔴';
      case 'sin_luz': return '💡';
      case 'desconectado': return '🔌';
      case 'vencimiento': return '⏰';
      case 'pago_recibido': return '💰';
      case 'recordatorio': return '📅';
      default: return '📢';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Notificaciones"
      >
        <FiBell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown de notificaciones */}
      {showDropdown && (
        <>
          {/* Overlay para cerrar al hacer clic fuera */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
          
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200">
            <div className="p-3 border-b flex justify-between items-center bg-gradient-to-r from-blue-50 to-white">
              <h3 className="font-semibold text-gray-700 flex items-center">
                <FiBell className="mr-2 text-blue-600" size={16} />
                Notificaciones
              </h3>
              {unreadCount > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  {unreadCount} {unreadCount === 1 ? 'nueva' : 'nuevas'}
                </span>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <FiBell className="mx-auto text-3xl text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">No hay notificaciones</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Las notificaciones aparecerán aquí
                  </p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif._id}
                    className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notif.isRead ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => {
                      setShowDropdown(false);
                      navigate('/notifications');
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg" title={notif.type}>
                        {getNotificationIcon(notif.type)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${
                          !notif.isRead ? 'text-gray-900' : 'text-gray-600'
                        }`}>
                          {notif.title || 'Notificación'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {notif.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">
                            {formatTime(notif.createdAt)}
                          </span>
                          {!notif.isRead && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                              Nueva
                            </span>
                          )}
                        </div>
                        {notif.metadata?.clientNumber && (
                          <p className="text-xs text-gray-400 mt-1">
                            Cliente #{notif.metadata.clientNumber}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-2 border-t bg-gray-50">
              <button
                onClick={handleViewAll}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 py-2 hover:bg-blue-50 rounded-lg transition-colors font-medium"
              >
                Ver todas las notificaciones
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;